import { DataSource } from '@angular/cdk/collections';
import moment from 'moment';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, merge } from 'rxjs/operators';
import { saveAs } from 'file-saver';
import { EventEmitter } from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { MatPaginator } from '@angular/material/paginator';

export class T4DataSource extends DataSource<any> {
    _filterChange = new BehaviorSubject('');
    get filter(): string { return this._filterChange.value; }
    set filter(filter: string) { this._filterChange.next(filter); }

    public filteredData: any[] = [];
    public sortedData: any[] = [];
    public renderedData: any[] = [];

    public dataChanged = new EventEmitter<boolean>();

    constructor(public data: any[], private sorter: MatSort, private paginator: MatPaginator, public datasetName: string, private assembleFilterData: (item: any) => string = null) {
        super();
    }

    /** Connect function called by the table to retrieve one stream containing the data to render. */
    connect(): Observable<any[]> {
        // Listen for any changes in the base data, sorting, filtering, or pagination
        var displayDataChanges = [];
        displayDataChanges.push(this._filterChange);


        if (this.sorter) displayDataChanges.push(this.sorter.sortChange);
        if (this.paginator) displayDataChanges.push(this.paginator.page);

        return new Observable().pipe(
            merge(...displayDataChanges),
            map(() => {
                return this.update();
            }));
    }

  private update(): any[] {
    // Filter data
        this.filteredData = this.data.slice().filter((item: any) => {
            if (!this.filter) return true;

            if (item.Checked) return true;

            var filterParts = this.filter.split('&');
            for (var i = 0; i < filterParts.length; i++) {
                if (filterParts[i].trim() == '') continue;

                // Prepare filterable data
                let filterValue = filterParts[i].trim();
                let searchStr = "";
                if (filterValue.indexOf('=') != -1) { // Specific field
                    var filterField = filterValue.split('=')[0].trim();
                    filterValue = filterValue.split('=')[1].trim();

                    if(item[filterField]) searchStr = item[filterField] + '|';
                } else { // Any field
                    if (this.assembleFilterData) searchStr = this.assembleFilterData(item);
                    else {
                        for (var prop in item) {
                            searchStr += item[prop] + "|";
                        }
                    }
                }

                if (searchStr.toLowerCase().indexOf(filterValue.toLowerCase()) == -1) return false;
            }

            return true
        });

        // Sort filtered data
        this.sortedData = this.getSortedData(this.filteredData.slice());

        // Grab the page's slice of the filtered sorted data.
        const startIndex = this.paginator ? this.paginator.pageIndex * this.paginator.pageSize : 0;
        const pageSize = this.paginator ? this.paginator.pageSize : 1000;

        this.renderedData = this.sortedData.splice(startIndex, pageSize);

        this.dataChanged.emit();
        return this.renderedData;
    }

    disconnect() { }

  getSortedData(data: any[]): any[] {
    if (!this.sorter || !this.sorter.active) { return data; }

    const sorted =  data.sort((a, b) => {
        let propA = null;
      let propB = null;

        if (this.sorter.active.includes('.')) {
          const ids = this.sorter.active.split('.');
          propA = a;
          propB = b;
          ids.forEach(function (x) {
            propA = propA ? propA[x] : null;
            propB = propB ? propB[x] : null;
          });
        } else {
          propA = a[this.sorter.active];
          propB = b[this.sorter.active];
        }

        if (typeof propA == 'string' || typeof propB == 'string') {
          propA = propA == undefined || propA == '' ? '1aaa' : propA.toLowerCase().trim();
          propB = propB == undefined || propB == '' ? '1aaa' : propB.toLowerCase().trim();;
        }
        else {
          propA = propA == undefined ? null : propA;
          propB = propB == undefined ? null : propB;
        }

        let valueA = isNaN(+propA!) ? propA : +propA!;
        let valueB = isNaN(+propB!) ? propB : +propB!;

        return (valueA! < valueB! ? -1 : 1) * (this.sorter.direction == 'desc' ? -1 : 1);
    });

    return this.sortWithSelectionPosition(sorted);

  }

  private sortWithSelectionPosition(data: any[]): any[] {
    if (this.filter && this.filter.trim().length > 0) {
      return [
        ...data.filter(row => row.Checked),
        ...data.filter(row => !row.Checked)
      ];
    }
    return data;
  }

  public export(transformations: any = null, ignoreNonTransformedFields: boolean = false) {
    var content = "";
    var globalTransformations = {
      ObjectLocation: x => x.ObjectLocation.Name,
      ServiceLocation: x => x.ServiceLocation?.Name,
      RentalObject: x => x.RentalObject?.Title,
      RentalLocation: x=> x.RentalLocation?.Name,
    };

    //If we have a template, we use those as headers, otherwise we take all from the data object. 
    var headers: string[];
    if (ignoreNonTransformedFields && transformations) {
      headers = Object.keys(transformations);
    } else {
      // Collect all unique keys from all rows
      const headerSet = new Set<string>();
      for (const row of this.data) {
        Object.keys(row).forEach(key => headerSet.add(key));
      }
      headers = Array.from(headerSet);
    }

    // Write headers to CSV/Excel content
    for (var i = 0; i < headers.length; i++)
      content += headers[i] + ';';
      content += "\r\n";

    // Iterate over data to extract specified properties or all properties if template is not provided
    for (var i = 0; i < this.data.length; i++) {
      for (var j = 0; j < headers.length; j++) {
        var property = headers[j];
        var val;

        if (transformations && typeof transformations[property] === 'function') {
          val = transformations[property](this.data[i]);
        } else if (globalTransformations && typeof globalTransformations[property] === 'function') {
          val = globalTransformations[property](this.data[i]);
        }
        else {
          val = this.data[i][property];
        }

        // Process the value based on its type and append to content
        if (val !== undefined && val !== null) {
          if (val instanceof Date) {
            content += val.toISOString();
          } else if (val instanceof moment) {
            content += (val as moment.Moment).format("YYYY-MM-DD HH:mm");
          } else if (typeof val === "string") {
            content += "\"" + val.replace(/\r?\n|\r/g, "").replace("\"", "\"\"") + "\"";
          } else if (typeof val === "number") {
            content += val.toString().replace(".", ",");
          } else if (val.toString) {
            content += val.toString();
          }
        }
        content += ";";
      }
      content += "\r\n";
    }

    // Create Blob and save as CSV file
    var blob = new Blob([content.replace(".", ",")], {
      type: "text/csv;charset=utf-8"
    });

    var filename = "Turbo-data.csv";
    if (this.datasetName) filename = this.datasetName + ".csv"

    saveAs(blob, filename);
  }

  public sortNestedProperty(data:any ,activeSorter: any) {
    const res = activeSorter.split('.').reduce((acc, key) => acc && acc[key], data) as | string | number;
    return res;
  }

}
